/**
 * take-bridge.ts
 *
 * Ponte entre o AnimationPreset (editor de takes) e o camera-animation.js (app real).
 *
 * Responsabilidades:
 *  1. Converter AnimationPreset → array `takes` do camera-animation.js
 *  2. Aplicar lightOverride a cada frame no loop de render (via hook que o Download.js chama)
 *  3. Expor window.TakeBridge para que o Download.js possa consumir sem imports
 *
 * INTEGRAÇÃO NO Download.js — adicione UMA linha antes do initCameraAnimation():
 *
 *   window.TakeBridge.applyPreset(window.activeTakePreset);
 *
 * E no loop de render (função render()), dentro do bloco `if (window.generatingTakes)`:
 *
 *   window.TakeBridge.applyFrameLighting(animationState.currentTime);
 *
 * Tudo mais continua igual.
 */

import type { AnimationPreset, Take, ResolvedFrame } from './take-types'
import { resolveFrame, getTotalDuration } from '../../../lib/animation-engine'

// ─── Tipo interno que o camera-animation.js espera ───────────────────────────

interface LegacyTake {
  name: string
  startTime: number
  endTime: number
  easing: string
  camera: {
    start: { position: { x: number; y: number; z: number }; lookAt: { x: number; y: number; z: number } }
    end:   { position: { x: number; y: number; z: number }; lookAt: { x: number; y: number; z: number } }
  }
  fov?: number
  _lightOverride?: {
    intensity: number
    color: string
    ambientIntensity: number
    exposure: number
    blink: { enabled: boolean; startOffset: number; duration: number; interval: number }
    fadeIn: boolean
  }
  onStart?: () => void
  onUpdate?: (progress: number) => void
}

// ─── Converte um Take do editor → LegacyTake do camera-animation.js ──────────

function toLegacyTake(take: Take, preset: AnimationPreset, index: number): LegacyTake {
  const isFirst = index === 0
  const isLast  = index === preset.takes.length - 1
  // Take penúltimo = onde as logos devem sair antes do take final
  const isPreLast = index === preset.takes.length - 2

  const legacy: LegacyTake = {
    name: take.name,
    startTime: take.startTime,
    endTime: take.endTime,
    easing: take.easing,
    camera: {
      start: {
        position: { ...take.camera.start.position },
        lookAt:   { ...take.camera.start.lookAt   },
      },
      end: {
        position: { ...take.camera.end.position },
        lookAt:   { ...take.camera.end.lookAt   },
      },
    },
    fov: take.camera.fovStart,
    _lightOverride: {
      intensity:        take.lighting.intensity,
      color:            take.lighting.color,
      ambientIntensity: take.lighting.ambientIntensity,
      exposure:         take.lighting.exposure,
      blink:            { ...take.lighting.blink },
      fadeIn:           take.fadeIn,
    },
  }

  // ── onStart por take ───────────────────────────────────────────────────────
  // Se o preset usa CloneSequence, sprites são gerenciadas pelo engine de clones
  // O take-bridge não toca nas sprites nesses presets
  const usesCloneSeq = !!(preset as any).useCloneSequence

  if (isFirst) {
    // Take 1: fade in das logos de canto no início do vídeo (todos os presets)
    const dur = take.endTime - take.startTime
    legacy.onStart = () => {
      const w = window as any
      if (!usesCloneSeq) w.currentPose && (w.currentPose.rotation.y = 0)
      // Fade in longo das sprites de canto (dura o take inteiro)
      if (w.sprite1) fadeSprite(w.sprite1, dur * 1000, true)
      if (w.sprite2) fadeSprite(w.sprite2, dur * 1000, true)
    }
  } else if (isPreLast && !isLast && !usesCloneSeq) {
    // Penúltimo take: fade out das logos antes do take final
    const dur = take.endTime - take.startTime
    legacy.onStart = () => {
      const w = window as any
      if (w.sprite1) fadeSprite(w.sprite1, dur * 1000 * 0.7, false)
      if (w.sprite2) fadeSprite(w.sprite2, dur * 1000 * 0.7, false)
    }
  }

  if (isLast) {
    // Último take: mostra logo centralizada
    // Se useCloneSequence: NÃO esconde modelo nem muda fundo — só mostra logo
    legacy.onStart = () => {
      const w = window as any

      // Fundo preto + esconde modelo + esconde sprites de canto (para todos os presets)
      if (w.scene?.background !== undefined) {
        w.backHolder = w.scene.background
        w.scene.background = new w.THREE.Color(0x000000)
      }
      if (w.currentPose) w.currentPose.visible = false
      if (w.cloned) w.cloned.visible = false  // clone interno (parte interna do modelo)
      // Esconde clones também se CloneSequence ativo
      if (usesCloneSeq && w.CloneSequence) {
        // Esconde todos os clones forçando dispose-like (sem destruir)
        w._cloneSeqHideAll = true
      }
      if (w.sprite1) { w.sprite1.material.opacity = 0; w.sprite1.visible = false }
      if (w.sprite2) { w.sprite2.material.opacity = 0; w.sprite2.visible = false }

      // Salva valores de iluminação ANTES do takes zerá-los (para restaurar depois)
      const _savedAmbIntensity  = w.ambLight?.intensity ?? 0.75
      const _savedAmbVisible    = w.ambLight?.visible ?? true
      const _savedExposure      = w.renderer?.toneMappingExposure ?? 1

      // Auto-restaura modelo ao final do take
      const _takeDurMs = (take.endTime - take.startTime) * 1000
      setTimeout(() => {
        if (w.currentPose) w.currentPose.visible = true
        if (w.cloned) w.cloned.visible = true
        if (w.scene && w.backHolder !== undefined) {
          w.scene.background = w.backHolder
          delete w.backHolder
        }
        // Restaura valores originais (não valores fixos)
        if (w.ambLight) { w.ambLight.intensity = _savedAmbIntensity; w.ambLight.visible = _savedAmbVisible }
        if (w.renderer) w.renderer.toneMappingExposure = _savedExposure
      }, _takeDurMs + 300)

      // Sprites nos cantos — gerenciadas pelo CloneSequence.tick para presets com clone
      // Para presets normais, o fade já foi feito no isFirst/isPreLast
      // (não precisa fazer nada aqui)

      // logoMesh: centraliza na câmera e mostra (sem fade — instântaneo para CloneSequence)
      if (w.logoMesh) {
        // Recalcula scale para o FOV atual (evita distorção)
        if (typeof w._calcLogoScale === 'function') {
          const s = w._calcLogoScale(w.camera, 0.35)
          w.logoMesh.scale.set(s.w, s.h, 1)
        }
        // Garante que está como filho da câmera e centralizado
        if (!w.logoMesh.parent || w.logoMesh.parent !== w.camera) {
          if (w.logoMesh.parent) w.logoMesh.parent.remove(w.logoMesh)
          w.camera.add(w.logoMesh)
        }
        w.logoMesh.position.set(0, 0, -1)
        if (usesCloneSeq) {
          // CloneSequence: logo aparece instantaneamente, sem fade
          w.logoMesh.material.opacity = 1
          w.logoMesh.material.transparent = false
          w.logoMesh.visible = true
        } else {
          // Preset normal: fade in suave de 1.5s
          w.logoMesh.material.opacity = 0
          w.logoMesh.material.transparent = true
          w.logoMesh.visible = true
          const start = performance.now()
          const dur = 1500
          ;(function tick(now: number) {
            const t = Math.min((now - start) / dur, 1)
            if (w.logoMesh) w.logoMesh.material.opacity = t
            if (t < 1) requestAnimationFrame(tick)
          })(performance.now())
        }
      }
    }
  }

  return legacy
}

// Helper: replica o fadeSprite do camera-animation.js para uso no bridge
function fadeSprite(sprite: any, duration = 1000, fadeIn = true) {
  const start = performance.now()
  const initialOpacity = sprite.material.opacity
  const targetOpacity  = fadeIn ? 1 : 0
  sprite.visible = true

  function animate(time: number) {
    const t = Math.min((time - start) / duration, 1)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const THREE = (window as any).THREE
    sprite.material.opacity = THREE
      ? THREE.MathUtils.lerp(initialOpacity, targetOpacity, t)
      : initialOpacity + (targetOpacity - initialOpacity) * t
    if (!fadeIn && t >= 1) sprite.visible = false
    if (t < 1) requestAnimationFrame(animate)
  }
  requestAnimationFrame(animate)
}

// ─── Cache do preset ativo ────────────────────────────────────────────────────

let _activePreset: AnimationPreset | null = null

// ─── applyPreset: converte e injeta no camera-animation.js ───────────────────

function applyPreset(preset: AnimationPreset): void {
  const w = window as any
  if (!w.CameraAnimation?.takes) {
    console.warn('[TakeBridge] window.CameraAnimation não está disponível ainda.')
    return
  }

  _activePreset = preset

  // Se preset usa CloneSequence, garante que logoMesh começa oculta
  if ((preset as any).useCloneSequence) {
    const w = window as any
    if (w.logoMesh) { w.logoMesh.visible = false; if (w.logoMesh.material) w.logoMesh.material.opacity = 0 }
    if (w.sprite1)  { w.sprite1.visible = false }
    if (w.sprite2)  { w.sprite2.visible = false }
  }

  // Ajusta posições de câmera do preset ao tamanho real do modelo (CloneSequence)
  // window._cloneSeqCamZ é calculado no CloneSequence.init()
  const cloneCamZ = (window as any)._cloneSeqCamZ
  if ((preset as any).useCloneSequence && cloneCamZ) {
    const lookAtY = (window as any)._cloneSeqLookAtY ?? 1.1
    const camY    = (window as any)._cloneSeqCamY    ?? 1.2
    preset = {
      ...preset,
      takes: preset.takes.map(take => {
        if (!take.camera) return take
        const cam = take.camera
        const scale = cloneCamZ / 4.5
        const scalePos = (pos: any) => pos ? {
          ...pos,
          position: pos.position ? {
            x: pos.position.x * scale,
            y: camY,                    // Y da câmera = acima do centro visual
            z: pos.position.z * scale,
          } : pos.position,
          lookAt: pos.lookAt ? {
            ...pos.lookAt,
            y: lookAtY,                 // lookAt aponta para o centro visual do modelo
          } : pos.lookAt
        } : pos
        return {
          ...take,
          camera: {
            ...cam,
            start: scalePos(cam.start),
            end:   scalePos(cam.end),
          }
        }
      })
    }
  }

  const legacyTakes = preset.takes.map((t, i) => toLegacyTake(t, preset, i))

  // Substitui o array de takes in-place (o camera-animation.js usa a referência)
  const dest: any[] = w.CameraAnimation.takes
  dest.length = 0
  legacyTakes.forEach(t => dest.push(t))

  // ── Atualiza TOTAL_DURATION ───────────────────────────────────────────────
  // CRÍTICO: sem isso, o vídeo para em 42s (valor hardcoded do default)
  // mesmo que o preset tenha duração diferente.
  const total = getTotalDuration(preset)
  if (typeof w.CameraAnimation.setTotalDuration === 'function') {
    w.CameraAnimation.setTotalDuration(total)
  }
  // Fallback para versão sem setter (não deve acontecer após o patch)
  w.TAKES_TOTAL_DURATION = total

  // ── Armazena fadeDuration para initCameraAnimation ────────────────────────
  // O Download.js lê window._takeBridgeFadeDuration antes de chamar init
  w._takeBridgeFadeDuration = preset.fadeDuration ?? 0.5
  w._takeBridgeFadeColor    = preset.fadeColor ?? '#000000'

}

// ─── applyFrameLighting: chamado no loop de render ───────────────────────────
// Coloque esta chamada dentro de `if (window.generatingTakes)` no render() do Download.js

function applyFrameLighting(currentTime: number): void {
  if (!_activePreset) return

  const w = window as any
  const frame: ResolvedFrame = resolveFrame(_activePreset, currentTime)

  // Luz principal (rectLight1 = DirectionalLight no seu setup)
  if (w.rectLight1) {
    const onIntensity = frame.lightOn ? frame.lightIntensity : 0
    w.rectLight1.intensity = onIntensity
    w.rectLight1.color.set(frame.lightColor)
  }

  // Luz ambiente
  if (w.ambLight) {
    w.ambLight.intensity = frame.ambientIntensity
  }

  // Tone mapping exposure
  if (w.renderer) {
    w.renderer.toneMappingExposure = frame.exposure
  }

  // FOV (zoom) — interpola entre fovStart e fovEnd
  if (w.camera && _activePreset) {
    const take = _activePreset.takes[frame.takeIndex]
    if (take) {
      const fov = take.camera.fovStart + (take.camera.fovEnd - take.camera.fovStart) * frame.takeProgress
      if (Math.abs(w.camera.fov - fov) > 0.01) {
        w.camera.fov = fov
        w.camera.updateProjectionMatrix()
      }
    }
  }
}

// ─── getPresetForSave: retorna o preset ativo (para salvar no Supabase) ───────

function getActivePreset(): AnimationPreset | null {
  return _activePreset
}

function setActivePreset(preset: AnimationPreset): void {
  _activePreset = preset
}

// ─── Expõe no window para uso sem imports (Download.js é plain JS) ────────────

if (typeof window !== 'undefined') {
  ;(window as any).TakeBridge = {
    applyPreset,
    applyFrameLighting,
    getActivePreset,
    setActivePreset,
  }
}

export { applyPreset, applyFrameLighting, getActivePreset, setActivePreset }